
import React from 'react';
import { ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

export interface Article {
  id: string;
  title: string;
  source: string;
  date: string;
  snippet: string;
  url: string;
  sentiment?: "positive" | "negative" | "neutral";
}

interface ArticleCardProps {
  article: Article;
}

const ArticleCard = ({ article }: ArticleCardProps) => {
  // Determine card background based on sentiment
  const getBgColor = () => {
    switch (article.sentiment) {
      case "positive":
        return "bg-[#F2FCE2] border-green-200";
      case "negative":
        return "bg-red-50 border-red-200";
      default:
        return "bg-gray-50 border-gray-200";
    }
  };

  // Determine badge style based on sentiment
  const getBadgeStyle = () => {
    switch (article.sentiment) {
      case "positive":
        return "bg-green-100 text-green-800 hover:bg-green-100";
      case "negative":
        return "bg-red-100 text-red-800 hover:bg-red-100";
      default:
        return "bg-gray-100 text-gray-800 hover:bg-gray-100";
    }
  };

  return (
    <Card className={`overflow-hidden border transition-all ${getBgColor()}`}>
      <CardContent className="p-6">
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-redflags-dark mb-2">{article.title}</h3>
            <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
              <span>{article.source}</span>
              <span>•</span>
              <span>{new Date(article.date).toLocaleDateString()}</span>
            </div>
            {article.sentiment && (
              <Badge className={`mb-3 ${getBadgeStyle()}`} variant="outline">
                {article.sentiment.charAt(0).toUpperCase() + article.sentiment.slice(1)}
              </Badge>
            )}
          </div>
          <Button 
            variant="outline" 
            size="sm"
            asChild
            className="flex-shrink-0"
          >
            <a href={article.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1">
              <span className="text-xs">View Source</span>
              <ExternalLink className="w-3 h-3" />
            </a>
          </Button>
        </div>
        
        <p className="text-gray-700">{article.snippet}</p>
      </CardContent>
    </Card>
  );
};

export default ArticleCard;
